import { useEffect, useState } from "react";

import { CONTENT_HEADER_ID } from "@/app/ContentHeader";
import debounce from "@/util/debounce.mjs";
import globals from "@/util/global-whitelist.mjs";

/**
 * Magic constants for our app layout widths. If we find our
 * layout element widths changing often, we can attempt to
 * calculate these values at runtime.
 */
const COLLAPSE_NAV_TARGET_WIDTH = 1350;

export const useIsSmallViewport = () => {
  if (typeof globals.matchMedia === "undefined") return false;
  const mql = globals.matchMedia(`(max-width: ${COLLAPSE_NAV_TARGET_WIDTH}px)`);
  const initialState = mql.matches;

  const [value, setValue] = useState(initialState);
  const callback = debounce((event) => {
    setValue(event.matches);
  }, 500);

  useEffect(() => {
    mql.addEventListener("change", callback);
    return () => {
      mql.removeEventListener("change", callback);
    };
  }, []);

  return value;
};

export const useHeaderHeight = () => {
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const sizeObs = new ResizeObserver(
      ([
        {
          borderBoxSize: [{ blockSize }],
        },
      ]) => setHeaderHeight(blockSize),
    );
    const setHeader = (header) => {
      sizeObs.disconnect();
      sizeObs.observe(header);
    };

    const header = globals.document?.getElementById(CONTENT_HEADER_ID);
    if (header) {
      setHeader(header);
      return () => sizeObs.disconnect();
    }
    // no header was found, but we watch for one
    const mutObs = new MutationObserver((mutations) => {
      for (const { addedNodes } of mutations) {
        for (const node of addedNodes) {
          const header =
            node.id === CONTENT_HEADER_ID
              ? node
              : typeof node.getElementById === "function"
                ? node.getElementById(CONTENT_HEADER_ID)
                : null;
          if (header) {
            setHeader(header);
            mutObs.disconnect();
          }
        }
      }
    });
    mutObs.observe(globals.document.body, { childList: true, subtree: true });
    return () => {
      mutObs.disconnect();
      sizeObs.disconnect();
    };
  }, []);

  return headerHeight;
};
